import React, { useEffect, useRef } from 'react';
import { useConversation } from '../../../../contexts/ConversationContext';

import TextMessage from './TextMessage';
import AudioMessage from './AudioMessage';
import TypingIndicator from './TypingIndicator';

import { MessageType, ProductType } from '../../../../utils/types';

type ConversationType = {
  className?: string;
  messages: MessageType[];
  userImage: string;
  userBgColor?: string;
  textColor?: string;
};

// Product Card Component
const ProductCard: React.FC<{ product: ProductType; compact?: boolean }> = ({ product, compact }) => (
  <div className={`flex-shrink-0 ${compact ? 'w-28' : 'w-32'} bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100`}>
    <div className={`${compact ? 'h-20' : 'h-24'} bg-gray-50 flex items-center justify-center overflow-hidden`}>
      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
    </div>
    <div className="p-2">
      <p className={`font-medium text-gray-800 truncate ${compact ? 'text-[10px]' : 'text-xs'}`}>{product.name}</p>
      <div className="flex items-center gap-1 mt-1">
        <span className={`font-bold text-gray-900 ${compact ? 'text-[10px]' : 'text-xs'}`}>{product.price}</span>
        {product.discount && (
          <span className="text-[8px] text-green-600 bg-green-50 px-1 rounded">{product.discount}</span>
        )}
      </div>
    </div>
  </div>
);

// Product Carousel Component
const ProductCarousel: React.FC<{ products: ProductType[] }> = ({ products }) => (
  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none -mx-1 px-1">
    {products.map((product) => (
      <ProductCard key={product.id} product={product} />
    ))}
  </div>
);

// Selected Product Component (user's choice)
const SelectedProduct: React.FC<{ product: ProductType }> = ({ product }) => (
  <div className="flex items-center gap-2 bg-[#E2F7CB] rounded-lg p-2">
    <div className="w-12 h-12 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-800 truncate">{product.name}</p>
      <p className="text-xs font-bold text-gray-900">{product.price}</p>
    </div>
    <div className="text-green-600">
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  </div>
);

const Conversation: React.FC<ConversationType> = ({ className, messages, userImage, userBgColor, textColor }) => {
  const { displayedMessages, showTyping } = useConversation();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  };

  // conversation always scrolls to bottom
  useEffect(scrollToBottom, [displayedMessages]);

  const currentTypingType = displayedMessages.length < messages.length ? messages[displayedMessages.length]?.type : null;

  return (
    <div className={`flex flex-col overflow-y-auto p-4 ${className} bg-gray-100 rounded-lg scrollbar scrollbar-none`} ref={messagesEndRef}>
      {displayedMessages.map((msg, idx) => {
        if (msg.format === 'audio') {
          return (
            <TextMessage key={idx} type={msg.type} userImage={userImage} userBgColor={userBgColor} textColor={textColor}>
              <AudioMessage duration={msg.text} />
            </TextMessage>
          );
        } else if (msg.format === 'file') {
          return (
            <TextMessage key={idx} type={msg.type} userImage={userImage} userBgColor={userBgColor} textColor={textColor}>
              <div className="flex flex-row items-center p-2 bg-gray-200 rounded rounded-md h-8 w-full">
                <img className='h-5 w-5' alt="pdf" src="/assets/images/conversation/pdf-icon.svg" />
                <p className='pl-2'>{msg.text}</p>
              </div>
            </TextMessage>
          );
        } else if (msg.format === 'carousel' && msg.products) {
          return (
            <TextMessage key={idx} type={msg.type} userImage={userImage} userBgColor={userBgColor} textColor={textColor}>
              <div className="space-y-2">
                {msg.text && <p className="text-xs mb-2">{msg.text}</p>}
                <ProductCarousel products={msg.products} />
              </div>
            </TextMessage>
          );
        } else if (msg.format === 'selection' && msg.selectedProduct) {
          return (
            <div key={idx} className={`chat ${msg.type === 'user' ? 'chat-end' : 'chat-start'}`}>
              <div className='chat-image avatar online'>
                <div className='h-8 w-8 min-w-[2rem] rounded-full'>
                  {msg.type === 'receiver' ? (
                    <img src="/assets/images/conversation/bot-profile.png" alt="ai-profile" />
                  ) : (
                    <img src={`/assets/images/conversation/${userImage}`} alt="user-profile" />
                  )}
                </div>
              </div>
              <div className="chat-bubble p-2 bg-transparent shadow-none">
                <SelectedProduct product={msg.selectedProduct} />
              </div>
            </div>
          );
        }

        return (
          <TextMessage key={idx} text={msg.text} type={msg.type} userImage={userImage} userBgColor={userBgColor} textColor={textColor} />
        );
      })}

      {currentTypingType && (
        <div className={`flex ${currentTypingType === 'user' ? 'self-end' : 'self-start'} items-end`}>
          {currentTypingType === 'receiver' ? <img src="/assets/images/conversation/bot-profile.png" alt="ai-profile" className="h-8 w-8 mr-2 rounded-full" /> : null}
          <TypingIndicator show={showTyping} type={currentTypingType} />
          {currentTypingType === 'user' ? <img src={`/assets/images/conversation/${userImage}`} alt="user_image" className="h-8 w-8 ml-2 rounded-full" /> : null}
        </div>
      )}
    </div>
  );
};

export default Conversation;
